import { useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  CarePlanGoalResponse,
  CarePlanProblemResponse,
  CarePlanResponse,
} from '~/lib/types/carePlan/carePlans';
import { useGetProblemTypesGoalStatusesMapQuery } from '~/store/services/refCarePlanEnums';
import { AddCustomInterventionModal } from './AddCustomInterventionModal';
import { CarePlanGoalsSection } from './CarePlanGoalsSection';
import { toCarePlanGoalTagDropdownOptions } from './CarePlanGoalTagDropdownOptions';

type Props = {
  problems: CarePlanProblemResponse[];
  treatmentEpisodeId: string;
  carePlan: CarePlanResponse;
  onEditProblemClick: (problem: CarePlanProblemResponse) => void;
  onEditGoalClick: (goal: CarePlanGoalResponse) => void;
  onRemoveGoalClick: (problemId: string) => void;
};

export const ActiveCarePlanCoOccurringSection = ({
  problems,
  treatmentEpisodeId,
  carePlan,
  onEditProblemClick,
  onEditGoalClick,
  onRemoveGoalClick,
}: Props) => {
  const {
    carePlanM4MedPsychProblems: isMedPsychProblemsEnabled,
    carePlanM4CustomInterventions,
  } = useFlags<LDFlags>();
  const { data: problemTypesGoalStatusesMap } =
    useGetProblemTypesGoalStatusesMapQuery();

  const [preselectedGoalId, setPreselectedGoalId] = useState<string | null>(
    null,
  );

  // Each problem type has its own goal-status set, so resolve options per type
  // rather than assuming medical and psychiatric stay in sync.
  const statusDropdownOptionsByProblemType = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(problemTypesGoalStatusesMap ?? {}).map(
          ([problemType, goalStatuses]) => [
            problemType,
            toCarePlanGoalTagDropdownOptions(
              goalStatuses,
              isMedPsychProblemsEnabled,
            ),
          ],
        ),
      ),
    [problemTypesGoalStatusesMap, isMedPsychProblemsEnabled],
  );

  return (
    <>
      <CarePlanGoalsSection
        sectionId="co-occurring-section"
        sectionTitle="Co-occurring problems"
        problems={problems}
        treatmentEpisodeId={treatmentEpisodeId}
        carePlanId={carePlan.id}
        statusDropdownOptionsByProblemType={statusDropdownOptionsByProblemType}
        displayProblemLabels={false}
        onEditProblemClick={onEditProblemClick}
        onEditGoalClick={onEditGoalClick}
        onAddInterventionClick={
          carePlanM4CustomInterventions
            ? (goal) => setPreselectedGoalId(goal.id)
            : undefined
        }
        onRemoveGoalClick={onRemoveGoalClick}
      />
      {preselectedGoalId && (
        <AddCustomInterventionModal
          carePlan={carePlan}
          onClose={() => setPreselectedGoalId(null)}
          preselectedGoalId={preselectedGoalId}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
    </>
  );
};
